import { FileButton, FileInput, Input } from "@mantine/core";
import {
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

import { Avatar, Group, H2, Stack, Text } from "@/components/ui/elements";

// 型ガード関数
function isFile(value: unknown): value is File {
  if (value === null || typeof value !== "object") {
    return false;
  }
  return value instanceof File;
}

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  name: FieldPath<FORM_TYPE>;
  errors?: FieldErrors<FORM_TYPE>;
  defaultImage?: string;
};

export function UserImageFields<FORM_TYPE extends FieldValues>({
  control,
  name,
  errors,
  defaultImage,
}: Props<FORM_TYPE>) {
  const { field } = useController({
    name,
    control,
  });

  const fieldIsFile = isFile(field.value);
  const imageSrc = fieldIsFile ? URL.createObjectURL(field.value) : null;
  return (
    <Stack>
      <H2>
        <Input.Label required fw="bold" mb={5}>
          プロフィール画像
        </Input.Label>
      </H2>
      <Stack>
        <Group justify="center" h={120} style={{ position: "relative" }}>
          <FileButton onChange={field.onChange} accept="image/png,image/jpeg">
            {(props) => (
              <Avatar
                variant="outline"
                color="gray"
                h={120}
                w={120}
                {...props}
                src={imageSrc || defaultImage || null}
              />
            )}
          </FileButton>

          <FileInput
            variant="unstyled"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            ref={field.ref}
            accept="image/png,image/jpeg"
            valueComponent={(file) => {
              if (!file && !defaultImage) {
                return "未設定";
              }
              return "";
            }}
          />
        </Group>
        <Text ta="center" fw="bold" c="teal">
          タップして画像を変更
        </Text>
        {errors?.[name] && (
          <Input.Error>{`${errors[name]?.message}`}</Input.Error>
        )}
        <Input.Description>
          ※ 3か月以内に撮影されたものを推奨
          <br />※ お顔立ちが分かる真正面の写真を推奨 ※
          必ずご本人の写真をご登録ください。（アイコン、イメージ画像不可）
          <br />※ 応募先の施設様に勤務時のご本人確認に使用されます。
        </Input.Description>
      </Stack>
    </Stack>
  );
}
